import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Users, Plus, Trash, Shield, UserCog, ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const UserManagement = ({ user, setUser }) => {
  const [members, setMembers] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchWorkspaceAndMembers();
  }, []);

  const fetchWorkspaceAndMembers = async () => {
    try {
      const [wsRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/workspaces/my`),
        axios.get(`${API_URL}/workspaces/my/members`)
      ]);
      setWorkspace(wsRes.data);
      setMembers(membersRes.data.members || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch workspace data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }

    setInviting(true);
    try {
      const res = await axios.post(`${API_URL}/workspaces/${workspace.id}/invite`, {
        email,
        name,
        role
      });

      toast.success(`User invited successfully! ${res.data.temp_password ? `Temporary password: ${res.data.temp_password}` : ''}`);
      setInviteDialogOpen(false);
      setEmail('');
      setName('');
      setRole('viewer');
      fetchWorkspaceAndMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!confirm('Are you sure you want to remove this user?')) return;

    try {
      await axios.delete(`${API_URL}/workspaces/${workspace.id}/members/${userId}`);
      toast.success('User removed successfully');
      fetchWorkspaceAndMembers();
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/workspaces/${workspace.id}/members/${userId}/role?role=${newRole}`);
      toast.success('Role updated successfully');
      fetchWorkspaceAndMembers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleReset2fa = async (userId, userName) => {
    if (!confirm(`Reset 2FA for ${userName}? They will need to set up 2FA again.`)) return;
    try {
      await axios.post(`${API_URL}/workspaces/${workspace.id}/members/${userId}/reset-2fa`);
      toast.success(`2FA reset for ${userName}`);
      fetchWorkspaceAndMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset 2FA');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      workspace_admin: { color: 'bg-violet-100 text-violet-700', label: 'Admin' },
      editor: { color: 'bg-teal-100 text-teal-700', label: 'Editor' },
      viewer: { color: 'bg-zinc-100 text-zinc-700', label: 'Viewer' }
    };
    return badges[role] || badges.viewer;
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-zinc-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="user-management">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Team Management
              </h1>
              {workspace && (
                <p className="text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Manage users for <span className="font-semibold">{workspace.name}</span>
                </p>
              )}
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-violet-600 hover:bg-violet-700" data-testid="invite-user-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      className="mt-2"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      required
                      className="mt-2"
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="mt-2" data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer - Can view documents</SelectItem>
                        <SelectItem value="editor">Editor - Can edit documents</SelectItem>
                        <SelectItem value="workspace_admin">Admin - Full access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" disabled={inviting} data-testid="send-invite-button">
                    {inviting ? 'Inviting...' : 'Send Invite'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Members List */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="members-list">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-900">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-900">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-900">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-zinc-900">Joined</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-zinc-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {members.length > 0 ? (
                  members.map((member) => {
                    const badge = getRoleBadge(member.role);
                    return (
                      <tr key={member.id} className="hover:bg-zinc-50 transition-colors" data-testid={`member-row-${member.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                              <span className="text-violet-600 font-semibold">{member.name.charAt(0)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-zinc-900">{member.name}</span>
                              {member.totp_enabled && (
                                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" title="2FA enabled">
                                  <Shield className="h-2.5 w-2.5" /> 2FA
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-zinc-600">{member.email}</td>
                        <td className="px-6 py-4">
                          <Select
                            value={member.role}
                            onValueChange={(newRole) => handleUpdateRole(member.id, newRole)}
                            disabled={member.id === user.id}
                          >
                            <SelectTrigger className={`w-32 ${badge.color}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="workspace_admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          {new Date(member.joined_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {member.id !== user.id && (
                            <div className="flex items-center justify-end gap-1">
                              {member.totp_enabled && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReset2fa(member.id, member.name)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Reset 2FA"
                                  data-testid={`reset-2fa-${member.id}`}
                                >
                                  <ShieldOff className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveUser(member.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`remove-user-${member.id}`}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                      <p>No team members yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Role Descriptions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="h-5 w-5 text-violet-600" />
              <h3 className="font-bold text-zinc-900">Admin</h3>
            </div>
            <p className="text-sm text-zinc-600">Full access to manage team, documents, and settings</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <UserCog className="h-5 w-5 text-teal-600" />
              <h3 className="font-bold text-zinc-900">Editor</h3>
            </div>
            <p className="text-sm text-zinc-600">Can create, edit, and delete documents</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-5 w-5 text-zinc-600" />
              <h3 className="font-bold text-zinc-900">Viewer</h3>
            </div>
            <p className="text-sm text-zinc-600">Can only view and search documents</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserManagement;